import { useEffect, useState, useCallback } from 'react';
import { api } from '../api/client';
import { useToast } from '../components/Toast/ToastContext';
import './AdminMerchantsPage.css';

interface MerchantItem {
  id: string;
  username: string | null;
  telegramId: string;
  merchantFeePercent: number | null;
  createdAt: string;
  _count: { merchantApiTokens: number };
}

function FeeBadge({ feePercent }: { feePercent: number | null }) {
  if (feePercent === null) {
    return <span className="fee-badge fee-badge--default">default</span>;
  }
  if (feePercent === 0) {
    return <span className="fee-badge fee-badge--free">0% (free)</span>;
  }
  return <span className="fee-badge fee-badge--custom">{feePercent}%</span>;
}

function MerchantRow({
  merchant,
  onSave,
}: {
  merchant: MerchantItem;
  onSave: (id: string, feePercent: number | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState<string>(
    merchant.merchantFeePercent !== null ? String(merchant.merchantFeePercent) : '',
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const trimmed = inputValue.trim();
    const parsed = trimmed === '' ? null : parseFloat(trimmed);
    if (parsed !== null && (isNaN(parsed) || parsed < 0 || parsed > 100)) {
      setSaving(false);
      return;
    }
    await onSave(merchant.id, parsed);
    setSaving(false);
    setEditing(false);
  };

  const handleReset = async () => {
    setSaving(true);
    await onSave(merchant.id, null);
    setInputValue('');
    setSaving(false);
    setEditing(false);
  };

  return (
    <tr className="merchant-row">
      <td className="merchant-cell">{merchant.username ? `@${merchant.username}` : '—'}</td>
      <td className="merchant-cell merchant-cell--id">{merchant.telegramId}</td>
      <td className="merchant-cell merchant-cell--center">{merchant._count.merchantApiTokens}</td>
      <td className="merchant-cell merchant-cell--center">
        <FeeBadge feePercent={merchant.merchantFeePercent} />
      </td>
      <td className="merchant-cell merchant-cell--actions">
        {editing ? (
          <div className="fee-edit-row">
            <input
              type="number"
              className="fee-input"
              value={inputValue}
              min={0}
              max={100}
              step={0.01}
              placeholder="e.g. 0.10"
              onChange={(e) => setInputValue(e.target.value)}
              disabled={saving}
              autoFocus
            />
            <button
              className="btn btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '...' : 'Save'}
            </button>
            <button
              className="btn btn-reset"
              onClick={handleReset}
              disabled={saving}
              title="Reset to global default"
            >
              Default
            </button>
            <button
              className="btn btn-cancel"
              onClick={() => {
                setEditing(false);
                setInputValue(
                  merchant.merchantFeePercent !== null
                    ? String(merchant.merchantFeePercent)
                    : '',
                );
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button className="btn btn-edit" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

export default function AdminMerchantsPage() {
  const [merchants, setMerchants] = useState<MerchantItem[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAdminMerchants();
      setMerchants(data);
    } catch (err) {
      toast.error('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (id: string, feePercent: number | null) => {
    try {
      const updated = await api.setAdminMerchantFee(id, feePercent);
      setMerchants((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, merchantFeePercent: updated.merchantFeePercent } : m,
        ),
      );
      const msg =
        feePercent === null
          ? 'Reset to global default'
          : feePercent === 0
            ? 'Merchant fee set to 0% (free)'
            : `Merchant fee set to ${feePercent}%`;
      toast.success(msg);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update fee');
    }
  };

  return (
    <div className="admin-merchants">
      <div className="admin-merchants__header">
        <h2 className="admin-merchants__title">Merchant Fee Management</h2>
        <p className="admin-merchants__subtitle">
          Set a custom fee rate for merchant API orders. Does not affect Telegram/web payments.
        </p>
      </div>

      {loading ? (
        <div className="admin-merchants__loading">Loading merchants...</div>
      ) : merchants.length === 0 ? (
        <div className="admin-merchants__empty">No merchants with API tokens found.</div>
      ) : (
        <div className="admin-merchants__table-wrap">
          <table className="merchants-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Telegram ID</th>
                <th>Tokens</th>
                <th>API Fee</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {merchants.map((m) => (
                <MerchantRow key={m.id} merchant={m} onSave={handleSave} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
